import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const GATEWAY_BASE = '/api';

const CronJobs = () => {
  const { data: cronPayload, loading, error, retry } = useAsyncData(`${GATEWAY_BASE}/cron`);
  const { isStale, lastUpdated } = useStaleWarning(cronPayload);
  useAutoRefresh(retry);

  const jobs = cronPayload?.jobs || [];
  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <article className="panel cron-panel" aria-busy="true">
          <h2>Cron Jobs</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </article>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <article className="panel cron-panel" aria-live="assertive">
          <h2>Cron Jobs</h2>
          <p className="error-msg">Failed to load cron jobs.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </article>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <article className="panel cron-panel" aria-label="Cron jobs" aria-busy="false">
        <div className="panel-header">
          <h2>Cron Jobs</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh cron jobs">
              ↻
            </button>
          </div>
        </div>
        <ul className="cron-list" aria-label="Scheduled jobs">
          {jobs.length === 0 && <li className="empty">No scheduled jobs</li>}
          {jobs.map((job, i) => (
            <li key={job.jobId || job.id || i} className="cron-item">
              <strong>{job.name || 'Job ' + i}</strong>
              <span className={`state-${job.enabled ? 'on' : 'off'}`}>{job.enabled ? 'ON' : 'OFF'}</span>
              {job.schedule && <code>{JSON.stringify(job.schedule)}</code>}
            </li>
          ))}
        </ul>
      </article>
    </ErrorBoundary>
  );
};

export default CronJobs;
